import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { MenuItem } from '@atlaskit/editor-common/ui-menu';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { isOfflineMode } from '@atlaskit/editor-plugin-connectivity';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { Breakpoint } from '@atlaskit/editor-toolbar';
import type { EmojiProvider } from '@atlaskit/emoji';

import type { InsertBlockPlugin } from '../../../insertBlockPluginType';
import type { ToolbarInsertBlockButtonsConfig } from '../../../types';
import type { BlockMenuItem } from '../../ToolbarInsertBlock/create-items';
import { createItems } from '../../ToolbarInsertBlock/create-items';

import { filterDropdownItemsByBreakpoint } from './filterDropdownItems';

interface UseInsertButtonStateProps {
	api?: ExtractInjectionAPI<InsertBlockPlugin>;
	breakpoint?: Breakpoint | null;
	editorView?: EditorView;
	expandEnabled?: boolean;
	horizontalRuleEnabled?: boolean;
	insertMenuItems?: MenuItem[];
	/** @see InsertBlockPluginOptions.itemFilter */
	itemFilter?: (item: MenuItem) => boolean;
	nativeStatusSupported?: boolean;
	numberOfButtons?: number;
	showElementBrowserLink?: boolean;
	tableSelectorSupported?: boolean;
	toolbarConfig?: ToolbarInsertBlockButtonsConfig;
}

export interface InsertButtonState {
	dropdownItems: BlockMenuItem[];
	emojiProvider?: EmojiProvider;
	isTypeAheadAllowed?: boolean;
}

export const useInsertButtonState = ({
	api,
	breakpoint,
	editorView,
	horizontalRuleEnabled,
	insertMenuItems,
	itemFilter,
	nativeStatusSupported,
	numberOfButtons,
	tableSelectorSupported,
	expandEnabled,
	showElementBrowserLink,
	toolbarConfig,
}: UseInsertButtonStateProps): InsertButtonState => {
	const { formatMessage } = useIntl();
	const isTypeAheadAllowed = useSharedPluginStateSelector(api, 'typeAhead.isAllowed');
	const imageUploadEnabled = useSharedPluginStateSelector(api, 'imageUpload.enabled');
	const mentionsSupported = !!useSharedPluginStateSelector(api, 'mention.mentionProvider');
	const canInsertMention = useSharedPluginStateSelector(api, 'mention.canInsertMention');
	const dateEnabled = useSharedPluginStateSelector(api, 'date.isInitialised');
	const placeholderTextEnabled = useSharedPluginStateSelector(
		api,
		'placeholderText.allowInserting',
	);
	const mediaAllowsUploads = useSharedPluginStateSelector(api, 'media.allowsUploads');

	const canInsertLink = useSharedPluginStateSelector(api, 'hyperlink.canInsertLink');
	const activeLinkMark = useSharedPluginStateSelector(api, 'hyperlink.activeLinkMark');
	const connectivityMode = useSharedPluginStateSelector(api, 'connectivity.mode');
	const emojiProvider = useSharedPluginStateSelector(api, 'emoji.emojiProvider');
	const availableWrapperBlockTypes = useSharedPluginStateSelector(
		api,
		'blockType.availableWrapperBlockTypes',
	);

	// Computed values
	const mediaUploadsEnabled = mediaAllowsUploads;
	const mediaSupported = mediaAllowsUploads !== undefined;
	const isEditorOffline = isOfflineMode(connectivityMode);
	const imageUploadSupported = !!api?.imageUpload;
	const mentionsDisabled = !canInsertMention;
	const linkSupported = canInsertLink !== undefined;
	const linkDisabled = !canInsertLink || !!activeLinkMark;
	const emojiDisabled = !emojiProvider;
	const actionSupported = !!editorView?.state.schema.nodes.taskItem;
	const decisionSupported = !!editorView?.state.schema.nodes.decisionItem;
	const layoutSectionEnabled = !!api?.layout;

	const [, allDropdownItems] = editorView?.state.schema
		? createItems({
				isTypeAheadAllowed: isTypeAheadAllowed,
				tableSupported: !!editorView?.state.schema.nodes.table,
				tableSelectorSupported,
				mediaUploadsEnabled,
				mediaSupported,
				isEditorOffline,
				imageUploadSupported,
				imageUploadEnabled,
				mentionsSupported,
				mentionsDisabled,
				actionSupported,
				decisionSupported,
				linkSupported,
				linkDisabled,
				emojiDisabled,
				hasEmojiPlugin: !!api?.emoji,
				hasMentionsPlugin: !!api?.mention,
				hasMediaPlugin: !!api?.media,
				nativeStatusSupported,
				dateEnabled,
				placeholderTextEnabled,
				horizontalRuleEnabled,
				layoutSectionEnabled,
				expandEnabled,
				showElementBrowserLink,
				emojiProvider,
				availableWrapperBlockTypes,
				insertMenuItems,
				itemFilter,
				schema: editorView?.state.schema,
				numberOfButtons: numberOfButtons || 0,
				formatMessage,
			})
		: [, []];

	const dropdownItems = useMemo(() => {
		if (!breakpoint || !toolbarConfig) {
			return allDropdownItems;
		}

		return filterDropdownItemsByBreakpoint(allDropdownItems, breakpoint, toolbarConfig);
	}, [allDropdownItems, breakpoint, toolbarConfig]);

	return {
		dropdownItems,
		emojiProvider,
		isTypeAheadAllowed,
	};
};
